import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  KeyboardArrowRight,
  Refresh as RefreshIcon,
  AddCircleOutline,
  ShoppingCart,
  AdminPanelSettings,
  CheckCircle,
  Cancel,
  ReportProblemOutlined,
} from '@mui/icons-material';
import { format, formatDistanceToNow, differenceInDays } from 'date-fns';
import PropTypes from 'prop-types';
import dataProvider from '../../../providers/dataProvider';
import { paymentTypeById } from '../../../constants/paymentType';
import paymentStatus, { paymentStatusById } from '../../../constants/paymentStatus';
import UpdatePaymentStatusModal from './UpdatePaymentStatusModal';

const CREATE = 'CREATE';
const GET_LIST = 'GET_LIST';
const GET_ALL = 'GET_ALL';

const fmtDate = (d) => (d ? format(new Date(d), 'MMM d, yyyy') : null);
const fmtAgo = (d) => (d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : null);

const localDayKey = (d) => {
  if (!d) return null;
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
};

const isSameLocalDay = (a, b) => {
  const ka = localDayKey(a);
  const kb = localDayKey(b);
  return !!ka && ka === kb;
};

const findSameDayPurchase = (grantedAt, products) => {
  if (!grantedAt || !Array.isArray(products) || products.length === 0) return null;
  return products.find((p) => isSameLocalDay(grantedAt, p.createdAt)) || null;
};

const REVOKED_REASON_LABEL = {
  admin: 'Revoked by admin',
  user_self: 'Self-revoked',
  product_cancel: 'Product canceled',
  product_refund: 'Refunded',
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'active', label: 'Active' },
  { key: 'revoked', label: 'Revoked' },
  { key: 'comp', label: 'Comp Grants' },
  { key: 'refunded', label: 'Refunded' },
];

const isComp = (up) => (up.sources || []).some((s) => !!s.adminGrantedByEmail);
const isRefunded = (up) => (up.sources || []).some((s) => s.revokedReason === 'product_refund');

const activeSources = (sources) => (sources || []).filter((s) => !s.revokedAt);
const hasSourceData = (up) => Array.isArray(up.sources) && up.sources.length > 0;

const sortedTrail = (up) => {
  const events = [];
  (up.sources || []).forEach((s) => {
    if (s.productPurchasedAt || (s.productName && s.grantedAt)) {
      events.push({
        kind: 'purchase',
        at: s.productPurchasedAt || s.grantedAt,
        label: `Purchased · ${s.productName || 'product'}`,
        by: 'via Stripe / SamCart',
        sourceId: s.id,
      });
    }
    if (s.adminGrantedByEmail) {
      events.push({
        kind: 'granted',
        at: s.grantedAt,
        label: 'Admin grant',
        by: `by ${s.adminGrantedByEmail}`,
        sourceId: s.id,
      });
    }
    if (s.revokedAt) {
      events.push({
        kind: 'revoked',
        at: s.revokedAt,
        label: REVOKED_REASON_LABEL[s.revokedReason] || 'Revoked',
        by: s.revokedByEmail ? `by ${s.revokedByEmail}` : 'by system',
        sourceId: s.id,
      });
    }
  });
  if (events.length === 0) {
    if (up.createdAt) {
      events.push({
        kind: 'granted',
        at: up.createdAt,
        label: 'Granted',
        by: 'source not recorded',
        sourceId: `up-${up.id}`,
      });
    }
    if (up.revokedAt) {
      events.push({
        kind: 'revoked',
        at: up.revokedAt,
        label: 'Revoked',
        by: 'by system',
        sourceId: `up-${up.id}-revoked`,
      });
    }
  }
  return events.sort((a, b) => new Date(b.at) - new Date(a.at));
};

const ProgramAccess = ({ userId: userIdProp }) => {
  const userId = Number(userIdProp);
  const [userPrograms, setUserPrograms] = useState([]);
  const [products, setProducts] = useState([]);
  const [allPrograms, setAllPrograms] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState(false);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [grantInput, setGrantInput] = useState(null);
  const [paymentStatusTarget, setPaymentStatusTarget] = useState(null);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [upRes, productsRes, programsRes, workoutsRes] = await Promise.all([
        // This screen renders revoke history and the Revoked filter, so it needs the
        // soft-deleted rows the endpoint hides by default.
        dataProvider(GET_ALL, `admin/users/${userId}/programs`, {
          urlParams: { includeRevoked: true },
        }),
        dataProvider(GET_LIST, 'admin/users/products', {
          pagination: { page: 1, perPage: 100 },
          sort: {},
          additionalFields: { userId },
        }),
        dataProvider(GET_LIST, 'programs', {
          pagination: { page: 1, perPage: 100 },
          sort: {},
        }),
        dataProvider(GET_LIST, `admin/users/${userId}/workout/session`, {
          pagination: { page: 1, perPage: 200 },
          sort: { field: 'startedAt', order: 'DESC' },
          additionalFields: { completed: true },
        }),
      ]);
      setUserPrograms(upRes.data.userPrograms || []);
      setProducts(productsRes.data.products || []);
      setAllPrograms(programsRes.data || []);
      setWorkouts(workoutsRes.data.sessions || workoutsRes.data || []);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const productByUserProductId = useMemo(() => {
    const map = {};
    products.forEach((p) => {
      map[p.id] = p;
    });
    return map;
  }, [products]);

  const lastWorkoutByProgramId = useMemo(() => {
    const map = {};
    workouts.forEach((w) => {
      const programId = w.programTrackSession?.programTrack?.program?.id;
      const at = w.completedAt || w.startedAt;
      if (!programId || !at) return;
      const prev = map[programId];
      if (!prev || new Date(at) > new Date(prev)) {
        map[programId] = at;
      }
    });
    return map;
  }, [workouts]);

  const latestWorkoutAt = useMemo(() => {
    const dates = workouts
      .map((w) => w.completedAt || w.startedAt)
      .filter(Boolean)
      .map((d) => new Date(d));
    if (!dates.length) return null;
    return dates.reduce((a, b) => (a > b ? a : b));
  }, [workouts]);

  const filtered = useMemo(() => {
    switch (filter) {
      case 'active':
        return userPrograms.filter((up) => !up.revokedAt);
      case 'revoked':
        return userPrograms.filter((up) => !!up.revokedAt);
      case 'comp':
        return userPrograms.filter(isComp);
      case 'refunded':
        return userPrograms.filter(isRefunded);
      default:
        return userPrograms;
    }
  }, [userPrograms, filter]);

  const counts = useMemo(
    () => ({
      all: userPrograms.length,
      active: userPrograms.filter((up) => !up.revokedAt).length,
      revoked: userPrograms.filter((up) => !!up.revokedAt).length,
      comp: userPrograms.filter(isComp).length,
      refunded: userPrograms.filter(isRefunded).length,
    }),
    [userPrograms],
  );

  const kpis = useMemo(() => {
    const active = userPrograms.filter((up) => !up.revokedAt);
    const lastSessionDate = latestWorkoutAt;
    let engagementLabel = 'No activity';
    let engagementColor = 'text.secondary';
    if (lastSessionDate) {
      const days = differenceInDays(new Date(), lastSessionDate);
      if (days <= 14) {
        engagementLabel = 'Active';
        engagementColor = 'success.main';
      } else if (days <= 60) {
        engagementLabel = 'Warm';
        engagementColor = 'warning.main';
      } else {
        engagementLabel = 'Cold';
        engagementColor = 'warning.dark';
      }
    } else if (userPrograms.length > 0) {
      engagementLabel = 'Cold';
      engagementColor = 'warning.dark';
    }
    return {
      active,
      lastSessionDate,
      engagementLabel,
      engagementColor,
    };
  }, [userPrograms, latestWorkoutAt]);

  const grantedProgramIds = useMemo(() => {
    const ids = new Set();
    userPrograms.forEach((up) => {
      if (!up.revokedAt) ids.add(up.program.id);
    });
    return ids;
  }, [userPrograms]);

  const grantableOptions = useMemo(
    () =>
      allPrograms
        .filter((p) => !grantedProgramIds.has(p.id))
        .map((p) => ({ id: p.id, label: p.name })),
    [allPrograms, grantedProgramIds],
  );

  const [errorMessage, setErrorMessage] = useState(null);

  const grant = useCallback(
    async (programId) => {
      setIsMutating(true);
      setErrorMessage(null);
      try {
        await dataProvider(CREATE, `programs/${programId}/grant`, { data: { userId } });
        await fetchAll();
      } catch (err) {
        setErrorMessage(typeof err === 'string' ? err : err?.message || 'Failed to grant access');
      } finally {
        setIsMutating(false);
      }
    },
    [userId, fetchAll],
  );

  const revoke = useCallback(
    async (programId) => {
      setIsMutating(true);
      setErrorMessage(null);
      try {
        await dataProvider(CREATE, `programs/${programId}/revoke`, { data: { userId } });
        await fetchAll();
      } catch (err) {
        setErrorMessage(typeof err === 'string' ? err : err?.message || 'Failed to revoke access');
      } finally {
        setIsMutating(false);
      }
    },
    [userId, fetchAll],
  );

  const handleToggleExpand = (id) => {
    setExpandedId((cur) => (cur === id ? null : id));
  };

  const handleGrantSelect = (_, value) => {
    if (value && value.id) {
      grant(value.id);
      setGrantInput(null);
    }
  };

  return (
    <Paper sx={{ mt: 2, mb: 3, overflow: 'hidden', position: 'relative' }}>
      {(isLoading || isMutating) && (
        <LinearProgress sx={{ position: 'absolute', left: 0, right: 0, top: 0, zIndex: 1 }} />
      )}

      {errorMessage && (
        <Box
          sx={{
            mx: 3,
            mt: 3,
            p: 1.5,
            border: 1,
            borderColor: 'error.main',
            borderRadius: 1,
            backgroundColor: 'error.light',
            color: 'error.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant="body2">{errorMessage}</Typography>
          <Button size="small" color="inherit" onClick={() => setErrorMessage(null)}>
            Dismiss
          </Button>
        </Box>
      )}

      <Box sx={{ p: 3, pb: 0 }}>
        <KpiStrip
          counts={counts}
          lastSessionDate={kpis.lastSessionDate}
          engagementLabel={kpis.engagementLabel}
          engagementColor={kpis.engagementColor}
        />
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontSize: '1.125rem', fontWeight: 600 }}>
            Program Access &amp; History
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Purchases, admin grants, and revocations — combined
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={fetchAll} disabled={isLoading || isMutating}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Tabs
        value={filter}
        onChange={(_, v) => setFilter(v)}
        sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        {FILTERS.map((f) => (
          <Tab
            key={f.key}
            value={f.key}
            label={
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{f.label}</span>
                <Chip
                  size="small"
                  label={counts[f.key] || 0}
                  sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: 12 } }}
                />
              </Stack>
            }
          />
        ))}
      </Tabs>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '22%' }}>Program</TableCell>
            <TableCell sx={{ width: '14%' }}>Status</TableCell>
            <TableCell sx={{ width: '22%' }}>Source</TableCell>
            <TableCell sx={{ width: '12%' }}>First Granted</TableCell>
            <TableCell sx={{ width: '18%' }}>Last Session</TableCell>
            <TableCell sx={{ width: '12%' }} />
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && userPrograms.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>
                  Loading program access…
                </Box>
              </TableCell>
            </TableRow>
          ) : filtered.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    {userPrograms.length === 0
                      ? 'No program access yet — grant one below.'
                      : 'No programs match this filter.'}
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            filtered.map((up) => (
              <ProgramRow
                key={up.id}
                userProgram={up}
                lastWorkoutAt={lastWorkoutByProgramId[up.program.id]}
                allProducts={products}
                expanded={expandedId === up.id}
                onToggle={() => handleToggleExpand(up.id)}
                onRevoke={() => revoke(up.program.id)}
                onReactivate={() => grant(up.program.id)}
                onUpdatePaymentStatus={(source) => {
                  const product = productByUserProductId[source.userProductId];
                  if (!product) return;
                  setPaymentStatusTarget({
                    id: product.id,
                    paymentStatus: product.paymentStatus,
                    paymentType: product.paymentType,
                  });
                }}
                getProductForSource={(source) => productByUserProductId[source.userProductId]}
                disabled={isMutating}
              />
            ))
          )}
        </TableBody>
      </Table>

      <Divider />

      <Box id="program-access-grant" sx={{ p: 3, backgroundColor: 'grey.100' }}>
        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 1, color: 'text.secondary', fontWeight: 600 }}
        >
          GRANT ADDITIONAL ACCESS
        </Typography>
        <Autocomplete
          options={grantableOptions}
          value={grantInput}
          onChange={handleGrantSelect}
          disabled={isMutating || grantableOptions.length === 0}
          getOptionLabel={(opt) => opt?.label || ''}
          isOptionEqualToValue={(opt, val) => opt.id === val?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder={
                grantableOptions.length === 0
                  ? 'Customer already has access to all programs'
                  : `Search ${grantableOptions.length} available programs to grant…`
              }
              slotProps={{
                input: {
                  ...params.InputProps,
                  startAdornment: (
                    <Box sx={{ display: 'flex', pl: 1 }}>
                      <AddCircleOutline fontSize="small" color="action" />
                    </Box>
                  ),
                },
              }}
              sx={{ backgroundColor: 'background.paper' }}
            />
          )}
        />
      </Box>

      <UpdatePaymentStatusModal
        isOpen={!!paymentStatusTarget}
        selectedData={paymentStatusTarget || {}}
        handleClose={() => setPaymentStatusTarget(null)}
        refreshUser={fetchAll}
      />
    </Paper>
  );
};

ProgramAccess.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

const KpiStrip = ({ counts, lastSessionDate, engagementLabel, engagementColor }) => {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        gap: 2,
        mb: 2,
      }}
    >
      <KpiCard
        label="Active Programs"
        value={counts.active}
        unit={`of ${counts.all} granted`}
        sub={`${counts.revoked} revoked${counts.comp ? ` · ${counts.comp} comp` : ''}`}
      />
      <KpiCard
        label="Last Workout"
        value={lastSessionDate ? fmtAgo(lastSessionDate) : 'Never'}
        sub={lastSessionDate ? fmtDate(lastSessionDate) : 'No sessions started'}
        muted={!lastSessionDate}
      />
      <KpiCard
        label="Engagement"
        value={engagementLabel}
        valueColor={engagementColor}
        sub={
          lastSessionDate
            ? `Based on most recent activity`
            : counts.all > 0
              ? 'Has access but never trained'
              : 'No data yet'
        }
        accent={
          engagementLabel === 'Cold' || engagementLabel === 'No activity' ? 'warn' : 'primary'
        }
      />
    </Box>
  );
};

KpiStrip.propTypes = {
  counts: PropTypes.shape({
    all: PropTypes.number,
    active: PropTypes.number,
    revoked: PropTypes.number,
    comp: PropTypes.number,
  }).isRequired,
  lastSessionDate: PropTypes.instanceOf(Date),
  engagementLabel: PropTypes.string.isRequired,
  engagementColor: PropTypes.string.isRequired,
};

KpiStrip.defaultProps = {
  lastSessionDate: null,
};

const KpiCard = ({ label, value, unit, sub, valueColor, accent, muted }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.25,
      border: 1,
      borderColor: 'divider',
      borderLeft: 3,
      borderLeftColor: accent === 'warn' ? 'warning.main' : 'primary.main',
      borderRadius: 1,
    }}
  >
    <Typography
      variant="overline"
      sx={{ color: 'text.secondary', letterSpacing: '0.05em', fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography
      variant="h5"
      sx={{
        mt: 0.5,
        fontWeight: 600,
        color: muted ? 'text.secondary' : valueColor || 'text.primary',
      }}
    >
      {value}
      {unit && (
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ ml: 0.5, fontWeight: 400 }}
        >
          {unit}
        </Typography>
      )}
    </Typography>
    {sub && (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  unit: PropTypes.string,
  sub: PropTypes.string,
  valueColor: PropTypes.string,
  accent: PropTypes.string,
  muted: PropTypes.bool,
};

KpiCard.defaultProps = {
  unit: '',
  sub: '',
  valueColor: '',
  accent: 'primary',
  muted: false,
};

const ProgramRow = ({
  userProgram: up,
  lastWorkoutAt,
  allProducts,
  expanded,
  onToggle,
  onRevoke,
  onReactivate,
  onUpdatePaymentStatus,
  getProductForSource,
  disabled,
}) => {
  const isRevoked = !!up.revokedAt;
  const active = activeSources(up.sources);
  const trail = sortedTrail(up);
  const sourcesKnown = hasSourceData(up);

  const purchaseSource = up.sources?.find((s) => s.userProductId);
  const sameDayProduct = findSameDayPurchase(up.createdAt, allProducts);
  const isKnownAdminGrant = sourcesKnown && (up.sources || []).length > 0 && !purchaseSource;
  const showDateMismatch = !isKnownAdminGrant && !sameDayProduct;

  const sourceSummary = (() => {
    if (!sourcesKnown) {
      if (sameDayProduct) {
        return {
          primary: `Legacy · likely ${sameDayProduct.name}`,
          secondary: `same-day purchase · ${fmtDate(sameDayProduct.createdAt)}`,
        };
      }
      return { primary: 'Legacy access', secondary: 'source not recorded' };
    }
    if (!active.length) {
      const s = up.sources[0];
      if (s.adminGrantedByEmail) {
        return { primary: 'Admin grant (revoked)', secondary: s.adminGrantedByEmail };
      }
      return { primary: s.productName || 'Purchase (revoked)', secondary: '' };
    }
    const first = active[0];
    if (first.adminGrantedByEmail) {
      return {
        primary: active.length > 1 ? `Admin grant +${active.length - 1}` : 'Admin grant',
        secondary: `by ${first.adminGrantedByEmail}`,
      };
    }
    return {
      primary: first.productName || 'Purchase',
      secondary: active.length > 1 ? `+${active.length - 1} more` : 'one-time purchase',
    };
  })();

  const lastSessionAt = lastWorkoutAt || null;
  const totalSessions = up.programTrack?.sessionsCount || up.program?.basicSessionCount || null;
  const completed = up.completedSessionsCount || 0;

  return (
    <>
      <TableRow
        hover
        onClick={onToggle}
        sx={{
          cursor: 'pointer',
          backgroundColor: expanded ? 'action.hover' : 'inherit',
          '& > td': { borderBottom: expanded ? 'none' : undefined },
        }}
      >
        <TableCell>
          <Stack direction="row" alignItems="center" spacing={1}>
            <KeyboardArrowRight
              sx={{
                transition: 'transform 200ms',
                transform: expanded ? 'rotate(90deg)' : 'none',
                color: expanded ? 'primary.main' : 'action.active',
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                color: isRevoked ? 'text.secondary' : 'text.primary',
              }}
            >
              {up.program.name}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>
          {isRevoked ? (
            <Chip
              size="small"
              icon={<Cancel sx={{ fontSize: 14 }} />}
              label={`Revoked ${fmtDate(up.revokedAt)}`}
              color="error"
              variant="outlined"
            />
          ) : (
            <Chip
              size="small"
              icon={<CheckCircle sx={{ fontSize: 14 }} />}
              label="Active"
              color="success"
            />
          )}
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1} alignItems="center">
            {sourceSummary.primary.toLowerCase().includes('admin') ? (
              <AdminPanelSettings fontSize="small" color="action" />
            ) : (
              <ShoppingCart fontSize="small" color="action" />
            )}
            <Box>
              <Typography variant="body2">{sourceSummary.primary}</Typography>
              {sourceSummary.secondary && (
                <Typography variant="caption" color="text.secondary">
                  {sourceSummary.secondary}
                </Typography>
              )}
            </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{fmtDate(up.createdAt) || '—'}</Typography>
          {up.createdAt && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {fmtAgo(up.createdAt)}
            </Typography>
          )}
          {sameDayProduct && (
            <Tooltip title={`Purchased ${fmtDate(sameDayProduct.createdAt)}`}>
              <Chip
                size="small"
                variant="outlined"
                color="success"
                label="Same day as purchase"
                sx={{ mt: 0.5, height: 18, '& .MuiChip-label': { px: 1, fontSize: 11 } }}
              />
            </Tooltip>
          )}
          {showDateMismatch && (
            <Tooltip
              title={
                allProducts && allProducts.length > 0
                  ? 'No userProduct purchase shares this grant date'
                  : 'Customer has no purchase records'
              }
            >
              <Chip
                size="small"
                variant="outlined"
                color="warning"
                icon={<ReportProblemOutlined sx={{ fontSize: 12 }} />}
                label="No same-day purchase"
                sx={{ mt: 0.5, height: 18, '& .MuiChip-label': { px: 1, fontSize: 11 } }}
              />
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          {lastSessionAt ? (
            <>
              <Typography variant="body2">{fmtDate(lastSessionAt)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {completed}
                {totalSessions ? ` / ${totalSessions}` : ''} sessions
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Never started
              </Typography>
              <Typography variant="caption" color="text.secondary">
                0{totalSessions ? ` / ${totalSessions}` : ''} sessions
              </Typography>
            </>
          )}
        </TableCell>
        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
          {isRevoked ? (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onReactivate();
              }}
            >
              Reactivate
            </Button>
          ) : (
            <Button
              size="small"
              color="error"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onRevoke();
              }}
            >
              Revoke
            </Button>
          )}
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
          <Collapse in={expanded} unmountOnExit>
            <Box sx={{ backgroundColor: 'grey.100', borderTop: 1, borderColor: 'divider' }}>
              <Box
                sx={{
                  p: 3,
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                  gap: 4,
                }}
              >
                <DetailBlock title="Grant Details">
                  <DetailRow
                    label="Source"
                    value={(() => {
                      if (!sourcesKnown) return 'Legacy access (source not recorded)';
                      if (purchaseSource) return 'Purchase';
                      if (up.sources?.[0]?.adminGrantedByEmail) return 'Admin comp grant';
                      return null;
                    })()}
                    missing="Not recorded"
                  />
                  {purchaseSource?.adminGrantedByEmail && (
                    <DetailRow label="Granted by" value={purchaseSource.adminGrantedByEmail} />
                  )}
                  {!purchaseSource && up.sources?.[0]?.adminGrantedByEmail && (
                    <DetailRow label="Granted by" value={up.sources[0].adminGrantedByEmail} />
                  )}
                  <DetailRow label="Granted on" value={fmtDate(up.createdAt) || '—'} />
                  {purchaseSource && (
                    <>
                      <DetailRow label="Product" value={purchaseSource.productName || '—'} />
                      <DetailRow
                        label="Purchased"
                        value={fmtDate(purchaseSource.productPurchasedAt) || '—'}
                      />
                    </>
                  )}
                  {!purchaseSource && !isKnownAdminGrant && (
                    <DetailRow
                      label="Same-day purchase"
                      value={
                        sameDayProduct
                          ? `${sameDayProduct.name} · ${fmtDate(sameDayProduct.createdAt)}`
                          : null
                      }
                      missing={
                        allProducts && allProducts.length > 0
                          ? 'None — likely comp grant or unrecorded'
                          : 'Customer has no purchases'
                      }
                    />
                  )}
                  {isRevoked && (
                    <>
                      <DetailRow label="Revoked" value={fmtDate(up.revokedAt)} />
                      <DetailRow
                        label="Revoked by"
                        value={up.sources?.find((s) => s.revokedAt)?.revokedByEmail}
                        missing="Not recorded"
                      />
                      <DetailRow
                        label="Reason"
                        value={
                          REVOKED_REASON_LABEL[
                            up.sources?.find((s) => s.revokedAt)?.revokedReason
                          ] || null
                        }
                        missing="Not recorded"
                      />
                    </>
                  )}
                </DetailBlock>

                <DetailBlock title="Engagement">
                  <DetailRow
                    label="Sessions Completed"
                    value={`${completed}${totalSessions ? ` / ${totalSessions}` : ''}`}
                  />
                  {up.programTrack?.name && (
                    <DetailRow label="Track" value={up.programTrack.name} />
                  )}
                  {up.nextSession?.name && (
                    <DetailRow label="Next Session" value={up.nextSession.name} />
                  )}
                  <DetailRow
                    label="Last Activity"
                    value={
                      lastSessionAt ? `${fmtDate(lastSessionAt)} · ${fmtAgo(lastSessionAt)}` : null
                    }
                    missing="No activity yet"
                  />
                  {up.schedule && Object.keys(up.schedule).length > 0 && (
                    <DetailRow label="Schedule" value={Object.keys(up.schedule).join(', ')} />
                  )}
                  {up.programComplete && <DetailRow label="Completed" value="Yes" />}
                </DetailBlock>

                <DetailBlock title="Audit Trail">
                  {trail.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No history recorded
                    </Typography>
                  ) : (
                    <AuditTrail events={trail} />
                  )}
                </DetailBlock>
              </Box>

              <Divider />

              {purchaseSource && getProductForSource(purchaseSource) && (
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  <PaymentStatusButton
                    product={getProductForSource(purchaseSource)}
                    onClick={() => onUpdatePaymentStatus(purchaseSource)}
                  />
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

ProgramRow.propTypes = {
  userProgram: PropTypes.shape({
    id: PropTypes.number,
    program: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      basicSessionCount: PropTypes.number,
      latestSession: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    }).isRequired,
    revokedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    completedSessionsCount: PropTypes.number,
    programComplete: PropTypes.bool,
    programTrack: PropTypes.shape({
      name: PropTypes.string,
      sessionsCount: PropTypes.number,
    }),
    nextSession: PropTypes.shape({ name: PropTypes.string }),
    lastSession: PropTypes.shape({
      createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    }),
    schedule: PropTypes.object,
    sources: PropTypes.array,
  }).isRequired,
  lastWorkoutAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  allProducts: PropTypes.array,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onRevoke: PropTypes.func.isRequired,
  onReactivate: PropTypes.func.isRequired,
  onUpdatePaymentStatus: PropTypes.func.isRequired,
  getProductForSource: PropTypes.func.isRequired,
  disabled: PropTypes.bool.isRequired,
};

ProgramRow.defaultProps = {
  lastWorkoutAt: null,
  allProducts: [],
};

const DetailBlock = ({ title, children }) => (
  <Box>
    <Typography
      variant="overline"
      sx={{
        color: 'text.secondary',
        fontWeight: 600,
        letterSpacing: '0.05em',
        display: 'block',
        mb: 1.5,
      }}
    >
      {title}
    </Typography>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        rowGap: 1,
        columnGap: 2,
        fontSize: 14,
      }}
    >
      {children}
    </Box>
  </Box>
);

DetailBlock.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const DetailRow = ({ label, value, missing }) => (
  <>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {value ? (
      <Typography variant="body2">{value}</Typography>
    ) : (
      <Stack direction="row" spacing={0.5} alignItems="center">
        <ReportProblemOutlined sx={{ fontSize: 14, color: 'warning.main' }} />
        <Typography variant="body2" sx={{ color: 'warning.main', fontStyle: 'italic' }}>
          {missing || 'Not recorded'}
        </Typography>
      </Stack>
    )}
  </>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  missing: PropTypes.string,
};

DetailRow.defaultProps = {
  value: null,
  missing: 'Not recorded',
};

const AuditTrail = ({ events }) => (
  <Box sx={{ position: 'relative', pl: 2.5 }}>
    <Box
      sx={{
        position: 'absolute',
        left: 6,
        top: 4,
        bottom: 4,
        width: 2,
        bgcolor: 'grey.300',
        borderRadius: 1,
      }}
    />
    <Stack spacing={1.5}>
      {events.map((e, i) => (
        <Box key={`${e.sourceId}-${e.kind}-${i}`} sx={{ position: 'relative' }}>
          <Box
            sx={{
              position: 'absolute',
              left: -19,
              top: 4,
              width: 12,
              height: 12,
              borderRadius: '50%',
              bgcolor:
                e.kind === 'granted'
                  ? 'success.main'
                  : e.kind === 'revoked'
                    ? 'error.main'
                    : 'primary.main',
              border: '2px solid',
              borderColor: 'background.paper',
              boxShadow: '0 0 0 1px',
              color:
                e.kind === 'granted'
                  ? 'success.main'
                  : e.kind === 'revoked'
                    ? 'error.main'
                    : 'primary.main',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {fmtDate(e.at)} · {fmtAgo(e.at)}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {e.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {e.by}
          </Typography>
        </Box>
      ))}
    </Stack>
  </Box>
);

AuditTrail.propTypes = {
  events: PropTypes.array.isRequired,
};

const PaymentStatusButton = ({ product, onClick }) => {
  if (!product) return null;
  const label = `${paymentTypeById[product.paymentType] || ''} · ${
    paymentStatusById[product.paymentStatus] || ''
  }${product.paymentStatus === paymentStatus.partialPaid.id ? ` (${product.paidCount})` : ''}`;
  return (
    <Button size="small" color="primary" onClick={onClick} startIcon={<ShoppingCart />}>
      Payment: {label}
    </Button>
  );
};

PaymentStatusButton.propTypes = {
  product: PropTypes.object,
  onClick: PropTypes.func.isRequired,
};

PaymentStatusButton.defaultProps = {
  product: null,
};

export default memo(ProgramAccess);
